import { useEffect, useState } from 'react';
import { useInvalidate, useList } from '@refinedev/core';
import { api } from '../lib/api.js';

interface PropertyPhotoRow {
  id: string;
  fileName: string;
  isPrimary: boolean;
}

interface PropertyPhotoGalleryProps {
  propertyId: string;
  canWrite: boolean;
}

/**
 * Already-uploaded PROPERTY_PHOTO documents, with a control to pick which one
 * is the hero/thumbnail image. Upload itself still goes through the generic
 * PhotoUpload below this on PropertyShow.tsx — this is a curation view, not
 * a second uploader.
 *
 * Signing a download URL per photo is a deliberate exception to
 * DocumentList.tsx's "no thumbnails" rule — that rule is about a generic file
 * table where a thumbnail buys little for the request cost. Here the entire
 * point is comparing a handful of photos to choose one, so the tradeoff runs
 * the other way.
 */
export function PropertyPhotoGallery({ propertyId, canWrite }: PropertyPhotoGalleryProps) {
  const invalidate = useInvalidate();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data } = useList<PropertyPhotoRow>({
    resource: 'documents',
    filters: [
      { field: 'propertyId', operator: 'eq', value: propertyId },
      { field: 'documentType', operator: 'eq', value: 'PROPERTY_PHOTO' },
    ],
    pagination: { mode: 'off' },
  });

  const photos = data?.data ?? [];
  if (photos.length === 0) return null;

  const sorted = [...photos].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));

  async function setPrimary(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await api(`/documents/${id}/primary`, { method: 'PATCH' });
      await invalidate({ resource: 'documents', invalidates: ['list'] });
      await invalidate({ resource: 'properties', invalidates: ['detail'] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not set that photo as primary.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="stack">
      {error ? <p className="alert alert--error">{error}</p> : null}
      <div className="photo-queue">
        {sorted.map((photo) => (
          <div key={photo.id} className="photo-queue__item">
            <PropertyPhotoThumb photoId={photo.id} fileName={photo.fileName} />
            <div className="photo-queue__controls">
              {photo.isPrimary ? (
                <span className="pill">Primary</span>
              ) : canWrite ? (
                <button
                  type="button"
                  className="button button--quiet"
                  disabled={busyId === photo.id}
                  onClick={() => void setPrimary(photo.id)}
                >
                  Set primary
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PropertyPhotoThumb({ photoId, fileName }: { photoId: string; fileName: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api<{ downloadUrl: string }>(`/documents/${photoId}/download`).then((result) => {
      if (!cancelled) setUrl(result.downloadUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [photoId]);

  return url ? (
    <img src={url} alt={fileName} className="photo-queue__thumb" />
  ) : (
    <div className="photo-queue__thumb photo-queue__thumb--loading" />
  );
}
